import {Navigate, useParams} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {enqueueSnackbar} from "notistack";
import {Alert, Box, Card, Chip, CircularProgress, Stack, Typography} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {
    CrossSaleAppKey,
    CrossSaleAppKeyValue,
    CrossSaleRequestedAppsResponse,
    findCrossSaleOpportunityBySlug,
} from "crqlar-react-common/dist/models/app/crossSaleOpportunityApps";
import {OrganizationAppService} from "crqlar-react-common/dist/services/app/organizationApp.service";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {buildAppLink} from "../layout/appNavigation";
import {findSidebarAppIcon} from "../layout/leftSidebar/SidebarContent";
import {PageShell} from "../layout/PageShell";
import CrossSalePreviewMock from "./CrossSalePreviewMock";
import {useCrossSaleOpportunities} from "./useCrossSaleOpportunities";
import bookingEnginePreview from "../../assets/media/images/crossSale/booking-engine-preview.png";
import crmPreview from "../../assets/media/images/crossSale/crm-preview.png";
import marketingPreview from "../../assets/media/images/crossSale/marketing-preview.png";
import restaurantPreview from "../../assets/media/images/crossSale/restaurant-preview.png";
import spaPreview from "../../assets/media/images/crossSale/spa-preview.png";
import surveysPreview from "../../assets/media/images/crossSale/surveys-preview.png";
import voiceNotePreview from "../../assets/media/images/crossSale/voice-note-preview.png";
import weeklyPlannerPreview from "../../assets/media/images/crossSale/weekly-planner-preview.png";

/*
    Static screenshots of the real module, shown faded instead of the blurred mock. Taken from a demo
    organization, so they carry no data of the organization looking at the preview. An app added to the
    allowlist without a screenshot falls back to the blurred mock.
 */
const PREVIEW_SCREENSHOTS: Partial<Record<CrossSaleAppKeyValue, string>> = {
    [CrossSaleAppKey.CRM]: crmPreview,
    [CrossSaleAppKey.MARKETING]: marketingPreview,
    [CrossSaleAppKey.SURVEYS]: surveysPreview,
    [CrossSaleAppKey.SPA]: spaPreview,
    [CrossSaleAppKey.RESTAURANT]: restaurantPreview,
    [CrossSaleAppKey.WEEKLY_PLANNER]: weeklyPlannerPreview,
    [CrossSaleAppKey.VOICE_NOTE]: voiceNotePreview,
    [CrossSaleAppKey.BOOKING_ENGINE]: bookingEnginePreview,
};

const httpStatusOf = (error: unknown): number | undefined => (error as {response?: {status?: number}})?.response?.status;

/*
    Locked preview (a faded screenshot, or a blurred mock) of a cross-selling app the organization is not subscribed to, with a single call to
    action that sends the lead to sales. Subscribed apps never land here: the page forwards to the real
    app (or home, when this user may not open it).
 */
const CrossSalePreviewPage = () => {
    const {slug} = useParams<"slug">();
    const opportunity = findCrossSaleOpportunityBySlug(slug);
    const {t, localize} = useLocalize();
    const queryClient = useQueryClient();
    const {ready, lockedApps, findPermittedInstallation} = useCrossSaleOpportunities();
    // A demo can be requested once per organization and app; an earlier request shows as already sent.
    const requestedQuery = useQuery("crossSaleRequestedApps", OrganizationAppService.getRequestedCrossSaleApps);
    const alreadyRequested = !!opportunity && !!requestedQuery.data?.appKeys.includes(opportunity.key);

    const interestMutation = useMutation(
        ["crossSaleInterest", opportunity?.key],
        () => OrganizationAppService.registerCrossSaleInterest(opportunity!.key),
        {
            // SENT and ALREADY_SENT both mean the server holds the lead; nothing else resolves the request
            onSuccess: () => {
                queryClient.setQueryData<CrossSaleRequestedAppsResponse>("crossSaleRequestedApps", previous => ({
                    appKeys: [...(previous?.appKeys ?? []).filter(key => key !== opportunity!.key), opportunity!.key],
                }));
                enqueueSnackbar(t("Thanks! Our team will get in touch with you soon."), {variant: "success"});
            },
            onError: error => {
                if (httpStatusOf(error) === 409) {
                    // subscribed in the meantime: refreshing the apps forwards this page to the real app
                    enqueueSnackbar(t("Your organization already has this module."), {variant: "info"});
                    queryClient.invalidateQueries("allOrganizationApps");
                    return;
                }
                enqueueSnackbar(t("We could not send your request. Please try again."), {variant: "error"});
            },
        }
    );

    if (!opportunity) return <Navigate to="/" replace/>;

    if (!ready || requestedQuery.isLoading) {
        return (
            <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320}}>
                <CircularProgress size={24}/>
            </Box>
        );
    }

    const locked = lockedApps.find(item => item.opportunity.key === opportunity.key);

    if (!locked) {
        const installation = findPermittedInstallation(opportunity);
        const target = installation?.app ? buildAppLink(installation.app, installation.orgApp) : "/";
        return <Navigate to={target} replace/>;
    }

    const Icon = findSidebarAppIcon(opportunity)?.Icon ?? AppsOutlinedIcon;
    const screenshot = PREVIEW_SCREENSHOTS[opportunity.key];
    const description = localize(locked.app?.descriptionLocalized);
    const sendInterest = () => {
        if (alreadyRequested || interestMutation.isLoading) return;
        interestMutation.mutate();
    };

    return (
        <PageShell
            icon={<Icon fontSize="inherit"/>}
            title={locked.name}
            documentTitle={locked.name}
            tabs={[]}
        >
            <Box sx={{position: "relative", flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden"}}>
                {screenshot ? (
                    <Box
                        component="img"
                        src={screenshot}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        // Fitted to the width, never cropped sideways: full-width modules keep their row
                        // labels and right-hand toolbar. Extra height is clipped at the bottom by the parent.
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "auto",
                            opacity: 0.6,
                            pointerEvents: "none",
                            userSelect: "none",
                        }}
                    />
                ) : (
                    <Box
                        aria-hidden="true"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            overflow: "hidden",
                            filter: "blur(6px)",
                            opacity: 0.8,
                            pointerEvents: "none",
                            userSelect: "none",
                        }}
                    >
                        <CrossSalePreviewMock appKey={opportunity.key}/>
                    </Box>
                )}

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        overflowY: "auto",
                        bgcolor: "rgb(255 255 255 / 35%)",
                    }}
                >
                    <Card
                        component="section"
                        aria-label={t("Preview of the {{appName}} module", {appName: locked.name})}
                        variant="outlined"
                        sx={{
                            width: "100%",
                            maxWidth: 480,
                            m: "auto",
                            p: {xs: 2.5, sm: 4},
                            borderRadius: 4,
                            textAlign: "center",
                            boxShadow: "0 12px 40px rgb(15 23 42 / 12%)",
                        }}
                    >
                        <Stack alignItems="center" gap={2}>
                            <Box
                                aria-hidden="true"
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "50%",
                                    bgcolor: "action.hover",
                                    color: CommonThemeColors.sys.primary.main,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <LockOutlinedIcon/>
                            </Box>

                            <Stack gap={1} alignItems="center">
                                <Chip size="small" label={t("Preview")}/>
                                <Typography variant="h5" fontWeight={700}>
                                    {locked.name}
                                </Typography>
                            </Stack>

                            {description && (
                                <Typography variant="body2" color={CommonThemeColors.sys.text.secondary}>
                                    {description}
                                </Typography>
                            )}

                            <Typography variant="body2" color={CommonThemeColors.sys.text.secondary}>
                                {t("You're not subscribed to {{appName}} yet. Tell us you're interested and our team will get in touch.", {appName: locked.name})}
                            </Typography>

                            {alreadyRequested ? (
                                <Alert severity="success" sx={{width: "100%", boxSizing: "border-box", textAlign: "left"}}>
                                    {t("Thanks! Our team will get in touch with you soon.")}
                                </Alert>
                            ) : (
                                <LoadingButton
                                    color={'secondary'}
                                    variant="contained"
                                    size="large"
                                    loading={interestMutation.isLoading}
                                    onClick={sendInterest}
                                    sx={{maxWidth: "100%", whiteSpace: "normal"}}
                                >
                                    {t("Request demo")}
                                </LoadingButton>
                            )}
                        </Stack>
                    </Card>
                </Box>
            </Box>
        </PageShell>
    );
};

export default CrossSalePreviewPage;
